import { createSlice } from "@reduxjs/toolkit";
import { StoreDispatch } from "../store";
import { immediateToast } from "izitoast-react";
import {
  createRecurringTransfer,
  createRecurringTransferRequest,
  searchRecurringTransfer,
  SearchRecurringTransferRequest,
  searchWallet,
  SearchWalletRequest,
  updateRecurringTransfer,
  updateRecurringTransferRequest
} from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";

export type RecurringTransferType = {
  destination_wallet: {
    name: string;
    wallet_id: string;
    description: string;
  };
  source_wallet: {
    name: string;
    wallet_id: string;
    description: string;
  };
  recurring_transaction_id: string;
  next_run_at: string;
  last_run_at: string;
  frequency: string;
  start_at: string;
  amount: number;
  created_at: string;
  updated_at: string;
  status: string;
  type: string;
};

const initialWallets: Wallet[] = [];
const initialRecurringTransfers: RecurringTransferType[] = [];
const recurringTransferSlice = createSlice({
  name: "recurringTransfer",
  initialState: {
    loading: false,
    walletLoading: false,
    recurringTransfers: {
      list: initialRecurringTransfers,
      total: 0
    },
    wallets: {
      list: initialWallets,
      total: 0
    },
    error: null
  },
  reducers: {
    fetchRecurringTransfersStart: (state) => {
      state.loading = true;
    },
    fetchRecurringTransfersSuccess: (state, action) => {
      state.loading = false;
      state.recurringTransfers.list = action.payload?.list ?? [];
      state.recurringTransfers.total = action.payload?.total ?? 0;
    },
    fetchRecurringTransfersFail: (state, action) => {
      state.loading = false;
      state.recurringTransfers.list = [];
      state.recurringTransfers.total = 0;
      state.error = action.payload;
    },
    createRecurringTransferStart: (state) => {
      state.loading = true;
    },
    createRecurringTransferSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    createRecurringTransferFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchWalletsStart: (state) => {
      state.walletLoading = true;
    },
    fetchWalletsSuccess: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = action.payload?.list ?? [];
      state.wallets.total = action.payload?.total ?? 0;
    },
    fetchWalletsFail: (state, action) => {
      state.walletLoading = false;
      state.wallets.list = [];
      state.wallets.total = 0;
      state.error = action.payload;
    },
    resetWalletListAction: (state) => {
      state.wallets.list = [];
      state.wallets.total = 0;
    }
  }
});

const {
  fetchRecurringTransfersStart,
  fetchRecurringTransfersSuccess,
  fetchRecurringTransfersFail,
  createRecurringTransferStart,
  createRecurringTransferSuccess,
  createRecurringTransferFail,
  fetchWalletsStart,
  fetchWalletsSuccess,
  fetchWalletsFail
} = recurringTransferSlice.actions;

export const { resetWalletListAction } = recurringTransferSlice.actions;

export const fetchRecurringTransfersAction = (data: SearchRecurringTransferRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchRecurringTransfersStart());
    try {
      const response = await searchRecurringTransfer(data);
      const structuredResponse = response.data.search_recurring_transaction.items;

      dispatch(
        fetchRecurringTransfersSuccess({
          list: structuredResponse,
          total: response.data.search_recurring_transaction.total_size
        })
      );
    } catch (error) {
      dispatch(fetchRecurringTransfersFail(error));
    }
  };
};

export const createRecurringTransferAction = (data: createRecurringTransferRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createRecurringTransferStart());
    try {
      await createRecurringTransfer(data);
      immediateToast("success", {
        message: "Recurring transfer created successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(createRecurringTransferSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(createRecurringTransferFail(error));
    }
  };
};

export const updateRecurringTransferAction = (data: updateRecurringTransferRequest, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(createRecurringTransferStart());
    try {
      await updateRecurringTransfer(data);
      immediateToast("success", {
        message: "Recurring transfer updated successfully",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(createRecurringTransferSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(createRecurringTransferFail(error));
    }
  };
};

export const fetchWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchWalletsStart());
    try {
      const response = await searchWallet(data);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        // status: wallet.wallet_status,
        walletType: wallet.wallet_type,
        name: wallet.name
        // participant: { role: "PARTICIPANT", fullName: wallet.description }
      }));

      dispatch(
        fetchWalletsSuccess({
          list: structuredResponse,
          total: structuredResponse.length
        })
      );
    } catch (error) {
      dispatch(fetchWalletsFail(error));
    }
  };
};

export default recurringTransferSlice.reducer;
